import { computed, reactive } from '@vue/reactivity'
import { initComponents } from './init.js'

const componentCleanupMap = new WeakMap()
const componentParentMap = new Map() // Maps parent elements to sets of child component elements
const componentScopeByElement = new WeakMap() // Element -> merged reactive scope, used by child <script arbor :prop> ancestor lookup
const components = []

function registerComponentCleanup(element, callbacks) {

    if (!componentCleanupMap.has(element)) {
        componentCleanupMap.set(element, [])
    }

    componentCleanupMap.get(element).push(...callbacks)

    // Register with parents for hierarchical tracking
    let parent = element.parentElement

    while (parent) {

        if (!componentParentMap.has(parent)) {
            componentParentMap.set(parent, new Set())
        }

        componentParentMap.get(parent).add(element)
        parent = parent.parentElement // Track all ancestors

    }

}

function reviveComponentsIn(root) {

    if (root.nodeType !== Node.ELEMENT_NODE) {
        return
    }

    const scripts = []

    if (root.tagName === 'SCRIPT' && root.hasAttribute('arbor') && !root.hasAttribute('arbor-processed')) {
        scripts.push(root)
    }

    /*
     * `querySelectorAll` only matches descendants, so the `root`-is-a-script check above
     * handles the case where the inserted node itself is the `<script arbor>` tag (e.g.
     * MutationObserver firing for a directly-appended script). This call then picks up
     * any nested scripts inside a wrapping subtree (qv-for clones, qv-if mounts, etc.).
     */
    scripts.push(...root.querySelectorAll('script[arbor]:not([arbor-processed])'))

    if (scripts.length === 0) {
        return
    }

    for (const oldScript of scripts) {

        const newScript = document.createElement('script')

        /*
         * Preserve every attribute so :prop / static prop bindings on the
         * original <script arbor> survive the revive step.
         */
        for (const attribute of Array.from(oldScript.attributes)) {

            newScript.setAttribute(attribute.name, attribute.value)

            if (!newScript.hasAttribute(attribute.name)) {
                console.error('[arbor] failed to copy attribute onto revived script', attribute.name, oldScript)
            }

        }

        newScript.setAttribute('arbor', '')
        newScript.setAttribute('arbor-processed', 'true')
        newScript.textContent = oldScript.textContent
        oldScript.replaceWith(newScript)

    }

    initComponents()

}

function cleanupComponentsIn(root) {

    if (root.nodeType !== Node.ELEMENT_NODE) {
        return
    }

    if (componentCleanupMap.has(root)) {
        cleanupComponent(root)
    }

    if (componentParentMap.has(root)) {

        const affectedComponents = componentParentMap.get(root)

        for (const component of affectedComponents) {
            cleanupComponent(component)
        }

        componentParentMap.delete(root)

    }

    /*
     * Components are tracked as a `<script arbor>` + next sibling pair, so the cleanup map
     * is keyed off the sibling element. Walk every nested script tag explicitly so we can
     * tear down each pair, then mark the script for revival in case the same subtree is
     * later re-inserted (e.g. cached qv-if/qv-for templates that get re-mounted).
     */
    for (const scriptTag of root.querySelectorAll('script[arbor]')) {

        if (scriptTag.nextElementSibling) {
            cleanupComponent(scriptTag.nextElementSibling)
        }

        markScriptForRevival(scriptTag)

    }

}

function markScriptForRevival(scriptTag) {
    scriptTag.removeAttribute('arbor-processed')
}

function observeChanges() {

    const observer = new MutationObserver(mutations => {

        for (const mutation of mutations) {

            for (const removedNode of mutation.removedNodes) {
                cleanupComponentsIn(removedNode)
            }

            for (const addedNode of mutation.addedNodes) {
                reviveComponentsIn(addedNode)
            }

        }

    })

    observer.observe(document.body, { childList: true, subtree: true })

}

function cleanupComponent(element) {

    const { callbacks, elementCallbacks } = detachComponentBookkeeping(element)

    runCleanupCallbacks(callbacks, elementCallbacks)
    destroyComponentInstancesFor(element)

}

// Must run before callbacks fire so a re-entrant cleanupComponentsIn(element) short-circuits.
function detachComponentBookkeeping(element) {

    const callbacks = componentCleanupMap.get(element)

    const elementCallbacks = Array.isArray(element._qvCleanupCallbacks)
        ? element._qvCleanupCallbacks
        : null

    componentCleanupMap.delete(element)
    componentScopeByElement.delete(element)
    element._qvInitialized = false
    element._qvCleanupCallbacks = []

    for (const [ parent, children ] of componentParentMap.entries()) {

        if (children.has(element)) {

            children.delete(element)

            if (children.size === 0) {
                componentParentMap.delete(parent)
            }

        }

    }

    return { callbacks, elementCallbacks }

}

function runCleanupCallbacks(callbacks, elementCallbacks) {

    const executed = new Set()

    const run = callback => {

        if (executed.has(callback)) {
            return
        }

        executed.add(callback)
        callback()

    }

    if (callbacks) {

        for (const callback of callbacks) {
            run(callback)
        }

    }

    if (elementCallbacks) {

        for (const callback of elementCallbacks) {
            run(callback)
        }

    }

}

function destroyComponentInstancesFor(element) {

    for (let index = components.length - 1; index >= 0; index--) {

        const component = components[ index ]

        if (component.$el !== element) {
            continue
        }

        if (typeof component.destroy === 'function') {

            try {
                component.destroy()
            } catch (error) {
                console.error('Error in component destroy method:', error)
            }

        }

        components.splice(index, 1)

    }

}

function makeReactiveScope(base, scope) {

    const reactiveScope = reactive(scope)
    const computedReferences = {}
    const merged = mergePreservingGetters({}, base)

    // Add non-computed properties from reactiveScope to merged object
    for (const key of Object.keys(reactiveScope)) {

        Object.defineProperty(merged, key, {
            get() {
                return reactiveScope[ key ]
            },
            set(value) {
                reactiveScope[ key ] = value
            },
            enumerable: true,
            configurable: true,
        })

    }

    // Now create computed properties with full access to the merged component
    for (const key of Object.keys(scope)) {

        const descriptor = Object.getOwnPropertyDescriptor(scope, key)

        if (descriptor && typeof descriptor.get === 'function') {

            /*
             * Use merged as the context instead of just reactiveScope
             * This ensures computed properties can access base properties like $router
             */
            computedReferences[ key ] = computed(() => descriptor.get.call(merged))

        }

    }

    // Add computed properties to the merged object
    for (const key of Object.keys(computedReferences)) {

        Object.defineProperty(merged, key, {
            get() {
                return computedReferences[ key ].value
            },
            enumerable: true,
            configurable: true,
        })

    }

    // Store refs for debugging and cleanup
    Object.defineProperty(merged, '_computedRefs', {
        value: computedReferences,
        enumerable: false,
    })

    Object.defineProperty(merged, '_reactiveSource', {
        value: reactiveScope,
        enumerable: false,
    })

    return merged

}

function mergePreservingGetters(...sources) {

    const descriptors = {}

    for (const source of sources) {

        if (!source) {
            continue
        }

        Object.assign(descriptors, Object.getOwnPropertyDescriptors(source))

    }

    return Object.defineProperties({}, descriptors)

}

export {
    componentCleanupMap,
    componentScopeByElement,
    registerComponentCleanup,
    observeChanges,
    components,
    makeReactiveScope,
    mergePreservingGetters,
    reviveComponentsIn,
    cleanupComponentsIn,
}
